import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { subscribe, useSnapshot } from "valtio";
import { proxyMap } from "valtio/utils";
import { getBlockMetadata } from "../block-utils.js";
import { registerBuiltinBlocks } from "../builtin-blocks.js";
import { InlineEditingContextProvider } from "../inline-editing.js";
import { EditorHighlights } from "./EditorHighlights.js";
import { BlockContext, EditableBlock } from "./EditorSupport.js";
import { resolveAcfBlockName } from "./block-templates.js";
import { blocksByTag } from "./blocks-by-tag.js";
import { editorConfigStore, getEditingPostInfo, watchEditorTemplate } from "./editor-config.js";
import { rootBlocks } from "./root-blocks.js";
function listenForHandleResize() {
    let interval = setInterval(() => {
        let viewportTarget = document.querySelector(".interface-interface-skeleton__content");
        if (viewportTarget) {
            const updateSize = () => {
                document.documentElement.style.setProperty("--vw100", viewportTarget.clientWidth + "px");
            };
            const ro = new ResizeObserver(updateSize);
            ro.observe(viewportTarget);
            clearInterval(interval);
        }
    }, 100);
}
export const blockMetaDescriptors = proxyMap();
export function whenEditorIsReady() {
    return new Promise((resolve) => {
        let ready = false;
        const unsubscribe = wp.data.subscribe(() => {
            // This will trigger after the initial render blocking, before the window load event
            // This seems currently more reliable than using __unstableIsEditorReady
            if (wp.data.select("core/editor").isCleanNewPost(undefined) ||
                wp.data.select("core/block-editor").getBlockCount() > 0) {
                if (ready)
                    return;
                ready = true;
                unsubscribe();
                resolve();
            }
        });
        setTimeout(() => {
            if (ready)
                return;
            unsubscribe();
            resolve();
        }, 1000);
    });
}
export function installEDGutenbergHooks() {
    import("../../../../../css/editor-styles.css");
    // Skip this if we're not in the block editor
    if (!wp.blockEditor)
        return;
    const withClientIdClassName = wp.compose.createHigherOrderComponent((BlockListBlock) => {
        return (props) => {
            const generateClass = useSnapshot(editorConfigStore).currentBlocksConfig?.generateBlockClassName;
            Object.assign(props, getBlockMetadata(props.name));
            props.isRootBlock = !props.rootClientId;
            let className = generateClass ? generateClass(props, getEditingPostInfo()) : "";
            return _jsx(BlockListBlock, { ...props, className: className });
        };
    }, "withClientIdClassName");
    wp.hooks.addFilter("editor.BlockListBlock", "ed", withClientIdClassName);
    // if (wp.blocks) {
    // }
    // Manage custom rich text formats
    setTimeout(() => {
        subscribe(editorConfigStore, () => {
            const formats = editorConfigStore.config?.customRichTextFormats;
            if (formats) {
                const activeFormats = wp.data.select("core/rich-text").getFormatTypes();
                formats.forEach((format) => {
                    if (activeFormats.some((f) => f.name === format.id)) {
                        wp.richText.unregisterFormatType(format.id);
                    }
                    const EditComponent = (props) => {
                        if (format.disabledByDefault) {
                            const explicitlyEnabled = props.contentRef.current
                                ?.getAttribute("data-allowed-formats")
                                ?.includes(format.id);
                            if (!explicitlyEnabled) {
                                return null;
                            }
                        }
                        return (_jsx(wp.components.Fill, { name: "BlockFormatControls", children: _jsx(wp.components.ToolbarButton, { icon: format.icon, title: format.title, onClick: () => {
                                    props.onChange(wp.richText.toggleFormat(props.value, {
                                        type: format.id,
                                    }));
                                }, isActive: props.isActive }) }));
                    };
                    wp.richText.registerFormatType(format.id, {
                        name: format.id,
                        title: format.title,
                        tagName: format.tagName ?? "span",
                        className: format.className,
                        interactive: false,
                        edit: EditComponent,
                        // @ts-ignore
                        disabledByDefault: format.disabledByDefault,
                    });
                });
            }
        });
    });
    listenForHandleResize();
    // Remove unwanted formatting options
    // https://developer.wordpress.org/block-editor/how-to-guides/format-api/
    const { removeFormatTypes } = wp.data.dispatch("core/rich-text");
    removeFormatTypes("core/image");
    removeFormatTypes("core/language");
    removeFormatTypes("core/keyboard");
    removeFormatTypes("core/text-color");
    whenEditorIsReady().then(() => {
        watchEditorTemplate();
    });
    // Recalculate registered blocks when rootBlocks change
    rootBlocks.listen();
    wp.hooks.addFilter("blocks.registerBlockType", "ed", (item, name) => {
        // Hook into ACF blocks, customising the edit mode.
        if (name.startsWith("acf/")) {
            const edit = item.edit;
            item.acf = {
                ...(item.acf ?? {}),
                mode: item.mode,
            };
            // Add an 'inline' object attribute, which will hold all inline contents
            item.attributes.inline = { type: "object" };
            item.attributes.values = { type: "object" };
            // Add className support to all ACF blocks
            const defaultStyleName = item.styles?.find((s) => s.isDefault)?.name;
            item.attributes.className = {
                type: "string",
                default: item.attributes?.className?.default ?? (defaultStyleName ? "is-style-" + defaultStyleName : undefined),
            };
            item.attributes.isFromTemplate = { type: "boolean" };
            item.supports.customClassName = false;
            item.edit = function (props) {
                const self = this;
                useEffect(() => {
                    ;
                    wp.data.dispatch("core/block-editor").setTemplateValidity(true);
                }, []);
                function getIndex() {
                    const { getBlockIndex } = wp.data.select("core/block-editor");
                    return getBlockIndex(props.clientId);
                }
                function getChildren() {
                    const { getBlock, getBlockOrder } = wp.data.select("core/block-editor");
                    const innerBlockIDs = getBlockOrder(props.clientId);
                    const children = innerBlockIDs.map((id) => {
                        return getBlock(id);
                    });
                    return children;
                }
                const [index, setIndex] = useState(getIndex);
                const [children, setChildren] = useState(getChildren);
                useEffect(() => {
                    return wp.data.subscribe(() => {
                        setIndex(getIndex());
                        const children = getChildren();
                        setChildren((current) => current.length === children.length
                            ? current.every((item, i) => item === children[i])
                                ? current
                                : children
                            : children);
                    }, []);
                }, [props.clientId, props.attributes]);
                return (_jsx(BlockContext.Provider, { value: { name, props }, children: _jsx(InlineEditingContextProvider, { values: props.attributes.inline || {}, innerBlocks: children, block: [name, props], index: index ?? -1, onChange: (attrs) => {
                            props.setAttributes({ ...props.attributes, inline: attrs });
                        }, insertBlocksAfter: props.insertBlocksAfter, children: _jsx(EditorHighlights, { enabled: true, clientId: props.clientId, children: edit.call(self, props) }) }) }));
            };
        }
        // Find PHP-defined 'tags' for core blocks.
        if ("ED_BLOCK_TAGS" in window && window.ED_BLOCK_TAGS) {
            const tags = window.ED_BLOCK_TAGS[name];
            if (tags) {
                item.tags = tags;
            }
        }
        // Ensure tags is always an array
        if (typeof item.tags === "string") {
            item.tags = [item.tags];
        }
        else if (!Array.isArray(item.tags)) {
            item.tags = [];
        }
        blocksByTag.add(item.name, item.tags);
        const isRootBlock = rootBlocks.isRootBlock(name, item.tags);
        return {
            ...item,
            get parent() {
                if (name === "core/block" || name === "core/slot-group")
                    return undefined;
                return blocksByTag.expand([...(item.parent ?? []), ...(isRootBlock ? ["core/post-content"] : [])]);
            },
            get transforms() {
                const result = {
                    from: item.transforms?.from ?? [],
                    to: item.transforms?.to ?? [],
                    ungroup: item.transforms?.ungroup,
                };
                const meta = blockMetaDescriptors.get(resolveAcfBlockName(name, false));
                if (meta?.transforms) {
                    result.from = [...(result.from ?? []), ...(meta.transforms.from ?? [])];
                    result.to = [...(result.to ?? []), ...(meta.transforms.to ?? [])];
                }
                return result;
            },
            get ancestor() {
                if (name === "core/block" || name === "core/slot-group")
                    return undefined;
                return item.ancestor ? blocksByTag.expand(item.ancestor) : undefined;
            },
            get allowedBlocks() {
                return item.allowedBlocks ? [...blocksByTag.expand(item.allowedBlocks), "core/slot-group"] : undefined;
            },
        };
    });
    if (wp.blocks) {
        registerBuiltinBlocks();
    }
    const acf = "acf" in window ? window.acf : {};
    acf.parseJSX = (html) => {
        let payload;
        try {
            payload = JSON.parse(html);
        }
        catch (err) {
            console.error(err);
            throw new Error("eddev: Error parsing block data from: \n" + html);
        }
        return _jsx(EditableBlock, { payload: payload ?? {} });
    };
}
